// Turbo (the dog on the skateboard) — floating help that opens a searchable FAQ
// speech bubble, mirroring the real drop form. FAQ is fetched live from the
// public FAQ API (same source + shape the drop form uses): { success, data:
// [{ title, questions: [{ question, answer_html | answer }] }] }.
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

const DOG_URL = "https://downloads.atxfilmlab.com/doggie_ver5.png";

interface FaqQuestion {
  question: string;
  answer?: string;
  answer_html?: string;
}
interface FaqCategory {
  title: string;
  questions: FaqQuestion[];
}

async function fetchFaq(): Promise<FaqCategory[]> {
  const res = await fetch("https://api.atxfilmlab.com/v1/faq");
  const json = await res.json();
  if (!json?.success || !json?.data) throw new Error("Failed to load FAQ data");
  return json.data as FaqCategory[];
}

export function FaqDog() {
  const [open, setOpen] = useState(false);
  const [helped, setHelped] = useState(false);
  const [search, setSearch] = useState("");

  const { data, isLoading, isError } = useQuery({
    queryKey: ["faq"],
    queryFn: fetchFaq,
    enabled: open,
    staleTime: 30 * 60 * 1000,
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data || [];
    return (data || [])
      .map((cat) => ({
        ...cat,
        questions: cat.questions.filter(
          (item) =>
            item.question.toLowerCase().includes(q) ||
            (item.answer_html || item.answer || "").toLowerCase().includes(q),
        ),
      }))
      .filter((cat) => cat.questions.length > 0);
  }, [data, search]);

  function toggle() {
    setHelped(true);
    setOpen((o) => !o);
  }

  return (
    <>
      {/* Turbo — in-flow at the bottom of the content (matches the real form's
          footer dog), so it never overlaps form controls or the sticky nav. */}
      <button type="button" onClick={toggle} aria-label="Help & FAQ" className="flex flex-col items-center gap-1 mx-auto mt-8 mb-2">
        <img
          src={DOG_URL}
          alt="Turbo"
          className={`w-20 h-20 object-contain drop-shadow ${open ? "" : "animate-[wiggle_2s_ease-in-out_infinite]"}`}
        />
        <span className="text-[12px] font-medium text-navy">
          {helped ? "Questions? Tap Turbo" : "Click me for help!"}
        </span>
      </button>

      {/* Modal */}
      {open && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/40" onClick={toggle} />
          <div className="absolute inset-x-3 bottom-24 top-16 max-w-md mx-auto bg-surface rounded-2xl shadow-modal flex flex-col overflow-hidden">
            <div className="px-4 pt-4 pb-2 border-b border-line">
              <div className="text-navy font-semibold text-[16px] flex items-center gap-1.5">
                Common questions and help <i className="ph ph-paw-print" />
              </div>
              <input
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="What can I help you find?"
                className="mt-2 w-full h-11 px-3 rounded-xl border border-line bg-canvas text-[16px] outline-none focus:border-navy"
              />
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-3">
              {isLoading && <p className="text-ink-subtle text-sm py-6 text-center">Loading FAQ…</p>}
              {isError && (
                <p className="text-sev-error text-sm py-6 text-center">Unable to load FAQ. Please try again later.</p>
              )}
              {filtered.map((cat) => (
                <div key={cat.title} className="mb-5">
                  <h3 className="text-navy font-semibold text-[14px] uppercase tracking-wide mb-2">{cat.title}</h3>
                  {cat.questions.map((item, i) => (
                    <div key={i} className="mb-3 pb-3 border-b border-line-soft last:border-0">
                      <div className="text-ink font-medium text-[14px] mb-1">{item.question}</div>
                      <div
                        className="faq-answer text-[13px] text-ink-muted leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: item.answer_html || item.answer || "" }}
                      />
                    </div>
                  ))}
                </div>
              ))}
              {!isLoading && !isError && filtered.length === 0 && (
                <p className="text-ink-subtle text-sm py-6 text-center">No matches — try different words.</p>
              )}
            </div>
            <button onClick={toggle} className="px-4 py-2.5 text-center text-[13px] text-navy border-t border-line">
              Click me to close! <i className="ph ph-paw-print" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
